import * as React from 'react';
import styled from 'styled-components';
import { Column } from '../column';
import { useWindowSize } from '../../../utils/window-size/hooks';
import { IResponsiveColumnProps } from './responsive-column-props';

const screenSizeRanges = {
    xs: { min: 1, max: 767 },
    sm: { min: 768, max: 991 },
    md: { min: 992, max: 1199 },
    lg: { min: 1200 }
};

function getColumnWidth(columnSize: number, colCount: number) {
    if (!columnSize) {
        return;
    }
    const columnWidth = columnSize / colCount * 100;
    return `width: ${columnWidth}%`;
}

const ColumnWithNoPadding = styled(Column)`
    padding: 0;
`;

function inRange(num: number, min: number, max = 99999) {
    return ((num - min) * (num - max) <= 0);
}

export function useDisplaySizeGroup() {
    const { windowWidth } = useWindowSize();
    const { xs, sm, md, lg } = screenSizeRanges;

    return React.useMemo(() => ({
        isXS: inRange(windowWidth, xs.min, xs.max),
        isSM: inRange(windowWidth, sm.min, sm.max),
        isMD: inRange(windowWidth, md.min, md.max),
        isLG: inRange(windowWidth, lg.min)
    }), [windowWidth, xs, sm, md, lg]);
}

function ResponsiveColumnBase(props: React.PropsWithChildren<IResponsiveColumnProps>) {
    const { className, hiddenxs, hiddensm, hiddenmd, hiddenlg } = props;

    const { isXS, isSM, isMD, isLG } = useDisplaySizeGroup();

    const hideColumnByScreenSize = React.useMemo(() => (
        (hiddenxs && isXS) || (hiddensm && isSM) || (hiddenmd && isMD) || (hiddenlg && isLG)
    ), [isXS, isSM, isMD, isLG, hiddenxs, hiddensm, hiddenmd, hiddenlg]);

    return hideColumnByScreenSize ? null :
        (
            <ColumnWithNoPadding className={className}>
                {props.children}
            </ColumnWithNoPadding>
        );
}

export const ResponsiveColumn = styled(ResponsiveColumnBase)`
    flex: unset;
    width: 100%;
    position: ${({ position }) => position ?? 'unset'};

    @media only screen and (max-width: 767px){
    ${({ xs, theme }) => (xs ? getColumnWidth(xs, theme.semantics.layout.columnsCount) : 'width:100%')};
    }

    @media only screen and (min-width: 768px) and (max-width: 991px){
        ${({ sm, theme }) => sm && getColumnWidth(sm, theme.semantics.layout.columnsCount)};
    }

    @media only screen and (min-width: 992px) and (max-width: 1199px){
        ${({ md, theme }) => md && getColumnWidth(md, theme.semantics.layout.columnsCount)};
    }

    @media only screen and (min-width: 1200px){
        ${({ lg, theme }) => lg && getColumnWidth(lg, theme.semantics.layout.columnsCount)};
    }
`;
